import React, { useEffect, useMemo } from 'react'
import { Check, X } from '@phosphor-icons/react'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Icon, IconButton } from '@shopflo/ui'
import AppLogo from '../../AppLogo'
import { classNames } from 'src/lib/utils/helpers'
import { currencyFormatter } from 'src/lib/utils/formatters'
import { toUpsellPlanOption } from 'src/lib/utils/appsIntegrationsHelpers'
import { PlanBadge } from 'src/lib/utils/ui/UpsellPlanBadge'
import {
  type UpsellActivationDialogProps,
  type UpsellPlanId,
  type UpsellPlanOption,
  UpsellActivityAction,
  UpsellPlanActionSource
} from 'src/lib/types/appsIntegrations'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'
import { useUpsellPlan } from 'src/lib/hooks/useUpsellPlan'

// Label + style for a plan card's button, given the merchant's current plan:
//   no plan → "Choose <plan>" on both · on Lite → Lite "Current", Plus "Upgrade" (primary)
//   on Plus → Lite "Switch to Lite", Plus "Current" · while a plan is being selected → "Upgrading…" for Plus, else "Activating…"
const getPlanButtonConfig = (
  plan: UpsellPlanOption,
  currentPlan: UpsellPlanId | null,
  isPlanLoading: boolean
): { label: string; variant: 'primary' | 'outline'; customClass?: string } => {
  const isUpgradeToPlus = currentPlan === 'lite' && plan.id === 'plus'
  const variant = isUpgradeToPlus ? 'primary' : 'outline'
  const customClass = currentPlan === plan.id ? 'opacity-40' : ''

  if (isPlanLoading) return { label: plan.id === 'plus' ? 'Upgrading…' : 'Activating…', variant }
  if (!currentPlan) return { label: `Choose ${plan.label}`, variant }
  if (currentPlan === plan.id) return { label: 'Current', variant, customClass }
  return { label: isUpgradeToPlus ? 'Upgrade' : `Switch to ${plan.label}`, variant }
}

export const UpsellActivationDialog: React.FC<UpsellActivationDialogProps> = ({ state, actions }) => {
  const {
    isOpen,
    appName = 'Upsell',
    logoUrl,
    isLoading = false,
    loadingPlanId = null,
    currentPlan = null,
    plans,
    entryPoint,
    isReopened = false
  } = state
  const { onClose, onSelectPlan } = actions
  const planOptions = useMemo(() => plans.map(toUpsellPlanOption), [plans])
  const { sendActivityLog } = useActivityLogs()
  const {
    state: { currentUpsellPlan }
  } = useUpsellPlan()

  useEffect(() => {
    if (isOpen && !isReopened) {
      sendActivityLog({
        entityName: EntityName.UPSELL,
        actionName: UpsellActivityAction.PLAN_MODAL_OPENED,
        metadata: { current_plan: currentUpsellPlan, entry_point: entryPoint }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleSelectPlan = (planId: UpsellPlanId) => {
    if (currentPlan === 'lite' && planId === 'plus') {
      sendActivityLog({
        entityName: EntityName.UPSELL,
        actionName: UpsellActivityAction.UPGRADE_CLICKED,
        metadata: { current_plan: currentUpsellPlan, to_plan: planId, source: UpsellPlanActionSource.PLAN_MODAL }
      })
    }
    if (currentPlan === 'plus' && planId === 'lite') {
      sendActivityLog({
        entityName: EntityName.UPSELL,
        actionName: UpsellActivityAction.SWITCH_TO_LITE_CLICKED,
        metadata: { current_plan: currentUpsellPlan, to_plan: 'lite', source: UpsellPlanActionSource.PLAN_MODAL }
      })
    }
    onSelectPlan(planId)
  }

  const getModalDescription = useMemo(() => {
    if (!currentPlan) {
      return 'Select a plan'
    } else if (currentPlan === 'lite') {
      return 'Upgrade'
    } else {
      return 'Plans'
    }
  }, [currentPlan])

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && !isLoading && onClose()}>
      <DialogContent className='bg-white w-[720px] max-w-[calc(100vw-2rem)] !rounded-2xl !p-0 !gap-0 [&>button]:hidden'>
        <DialogHeader className='px-5 pt-4 pb-0'>
          <div className='flex items-center justify-between gap-4'>
            <div className='flex items-center gap-2.5'>
              <AppLogo src={logoUrl} name={appName} size='sm' bare />
              <div>
                <p className='text-text-xs-medium text-zinc-500'>{appName}</p>
                <DialogTitle className='!text-[14px] !font-semibold !text-zinc-800 !leading-6'>
                  {getModalDescription}
                </DialogTitle>
              </div>
            </div>
            <IconButton
              variant='ghost'
              size='sm'
              IconComponent={X}
              aria-label='Close'
              className='-mr-1.5 text-zinc-500'
              onClick={onClose}
              disabled={isLoading}
            />
          </div>
        </DialogHeader>

        <div className='p-5 pt-3 grid grid-cols-2 gap-5'>
          {planOptions.map(plan => {
            const isPlanLoading = isLoading && loadingPlanId === plan.id
            const planButton = getPlanButtonConfig(plan, currentPlan, isPlanLoading)

            return (
              <div
                key={plan.id}
                className={classNames(
                  'flex flex-col justify-between rounded-2xl border py-3 px-5',
                  plan.highlighted ? 'border-purple-300 bg-purple-50' : 'border-zinc-200 bg-white'
                )}
              >
                <div>
                  <PlanBadge plan={plan.id} className='w-fit' />

                  <div className='mt-3 flex items-baseline gap-2'>
                    <span className='text-[22px] leading-7 font-semibold text-zinc-900'>
                      {currencyFormatter(plan.price)}/mo
                    </span>
                    {plan.discountLabel && plan.originalPrice && (
                      <>
                        <span className='text-text-sm-regular text-zinc-400 line-through'>
                          {currencyFormatter(plan.originalPrice)}
                        </span>
                        <span className='text-text-sm-semibold text-emerald-600'>{plan.discountLabel}</span>
                      </>
                    )}
                  </div>

                  <p className='mt-2 text-text-sm-regular text-zinc-500'>{plan.description}</p>

                  <div className='my-4 border-t border-dashed border-zinc-200' />

                  <ul className='flex flex-col gap-2.5'>
                    {plan.features.map(feature => (
                      <li key={feature} className='flex items-start gap-2'>
                        <Icon
                          IconComponent={Check}
                          size='xs'
                          iconProps={{ weight: 'bold' }}
                          className={classNames(
                            'mt-0.5 flex-shrink-0',
                            plan.highlighted ? 'text-violet-600' : 'text-zinc-700'
                          )}
                        />
                        <span className='text-text-xs-regular text-zinc-600'>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  variant={planButton.variant}
                  size='lg'
                  className={`w-full mt-6 ${planButton.customClass}`}
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={isLoading}
                >
                  {planButton.label}
                </Button>
              </div>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
